/**
 * Voicemail drop settings. Off by default.
 * Props: api (API.json), toast.
 * GET/PUT /api/tenant/voicemail-drop (tenant admin).
 */
const { useState, useEffect } = React;

const SAMPLE = { first_name: 'Alex', business_name: 'your company', callback_number: 'your callback number' };
const COMPLIANCE = 'Prerecorded messages to cell phones require prior express written consent under the TCPA. Leave this off unless every number you call has that consent.';

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.detail || data.message)) || (err && err.message) || fallback;
}

function preview(text) {
  return String(text || '').replace(/\{(first_name|business_name|callback_number)\}/g, (_match, key) => SAMPLE[key] || '');
}

export function VoicemailDropSettings({ api, toast }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('tts');
  const [text, setText] = useState('Hi {first_name}, this is {business_name}. Please call us back at {callback_number}.');
  const [audioUrl, setAudioUrl] = useState('');
  const [loadedText, setLoadedText] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await api('GET', '/api/tenant/voicemail-drop');
        if (cancel || !data) return;
        setEnabled(!!data.enabled);
        setMode(data.mode === 'audio' ? 'audio' : 'tts');
        if (data.text) {
          setText(data.text);
          setLoadedText(true);
        }
        setAudioUrl(data.audio_url || '');
      } catch (err) {
        if (!cancel) setError(errorMessage(err, 'Could not load voicemail drop settings.'));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api]);

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await api('PUT', '/api/tenant/voicemail-drop', {
        enabled,
        mode,
        text,
        audio_url: audioUrl
      });
      if (data) {
        setEnabled(!!data.enabled);
        setMode(data.mode === 'audio' ? 'audio' : 'tts');
        if (data.text) setText(data.text);
        setAudioUrl(data.audio_url || '');
      }
      toast(enabled ? 'Voicemail drop saved. It stays off for anyone who has not given written consent.' : 'Voicemail drop is off.');
      setLoadedText(true);
    } catch (err) {
      toast(errorMessage(err, 'Could not save voicemail drop.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="cmp" onSubmit={save}>
      <div>
        <h2 className="cmp-title">Voicemail drop</h2>
        <p className="cmp-lead">When an answering machine finishes its greeting, leave this message instead of hanging up.</p>
      </div>
      <p className="cmp-note">{COMPLIANCE}</p>
      {error ? <div className="cmp-error">{error}</div> : null}
      {loading ? <p className="cmp-meta">Loading…</p> : null}
      <label className="cmp-check">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        {' '}Leave a voicemail when a machine answers
      </label>
      <div className="cmp-actions">
        <label className="cmp-check">
          <input type="radio" name="voicemail-mode" checked={mode === 'tts'} onChange={() => setMode('tts')} />
          {' '}Read a message
        </label>
        <label className="cmp-check">
          <input type="radio" name="voicemail-mode" checked={mode === 'audio'} onChange={() => setMode('audio')} />
          {' '}Play an audio file
        </label>
      </div>
      {mode === 'audio' ? (
        <label className="cmp-field">
          Audio URL (https only)
          <input className="cmp-input cmp-grow" type="url" value={audioUrl} onChange={(e) => setAudioUrl(e.target.value)} placeholder="https://example.com/greeting.mp3" />
        </label>
      ) : (
        <div className="cmp-stack">
          <label className="cmp-field">
            Message
            <textarea className="cmp-input cmp-grow" rows={4} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          <p className="cmp-help">Use {'{first_name}'}, {'{business_name}'}, and {'{callback_number}'}.</p>
          <div className="cmp-preview">
            <div className="cmp-meta">Preview</div>
            {preview(text) || (loadedText ? '' : 'The preview appears here.')}
          </div>
        </div>
      )}
      <div>
        <button type="submit" className="cmp-btn cmp-btn--primary" disabled={busy || loading}>Save voicemail drop</button>
      </div>
    </form>
  );
}
